import { Flex, Box, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useHookstate } from "@hookstate/core";
import { Bundle } from "../../../../types";
import { appStore } from "../../../../hookstate/appStore";

interface BundleDetailsContributorsProps {
  bundle: Bundle;
}

export function BundleDetailsContributors({ bundle }: BundleDetailsContributorsProps) {
  const appInit = useHookstate(appStore.init).get({
    noproxy: true,
  });

  const bundleContributors = useMemo(() => {
    if (!bundle?.contributors || !appInit?.comic_contributor_types) return {};

    const contributorsMap: Record<
      string,
      Array<{ name: string; comic_contributor_type_id: number }>
    > = {};

    // If contributors is an array of contributor objects
    if (Array.isArray(bundle.contributors)) {
      bundle.contributors.forEach((contributor: any) => {
        if (contributor?.name && contributor?.comic_contributor_type_id) {
          // Find the contributor type name from appInit
          const contributorType = appInit.comic_contributor_types.find(
            (type: any) => type.id === contributor.comic_contributor_type_id,
          );

          if (contributorType?.name) {
            const role = contributorType.name;
            if (!contributorsMap[role]) {
              contributorsMap[role] = [];
            }

            // Check if contributor is already in the list to avoid duplicates
            const existingContributor = contributorsMap[role].find(
              (c) => c.name === contributor.name,
            );

            if (!existingContributor) {
              contributorsMap[role].push({
                name: contributor.name,
                comic_contributor_type_id:
                  contributor.comic_contributor_type_id,
              });
            }
          }
        }
      });
    }

    return contributorsMap;
  }, [bundle?.contributors, appInit?.comic_contributor_types]);

  if (Object.keys(bundleContributors).length === 0) {
    return null;
  }

  return (
    <Flex
      alignItems="flex-start"
      flexDirection="column"
      gap="12px"
      width="100%"
    >
      {Object.keys(bundleContributors).map((role) => {
        const contributorList = bundleContributors[role];
        return (
          <Flex key={role} flexDirection="column" gap="8px">
            <Text
              fontFamily="Roboto"
              fontSize="14px"
              fontWeight="500"
              lineHeight="18px"
              color="neutral.200"
            >
              {role}
            </Text>
            <Flex flexWrap="wrap" gap="6px">
              {contributorList.map((contributor) => (
                <Box
                  key={`${contributor.name}-${contributor.comic_contributor_type_id}`}
                  bg="transparent.white.10"
                  borderRadius="full"
                  py="8px"
                  px="12px"
                  width="fit-content"
                >
                  <Text
                    fontFamily="Roboto"
                    fontSize="8px"
                    fontWeight="bold"
                    lineHeight="10px"
                    letterSpacing="0.5px"
                    color="dune.700"
                    textTransform="uppercase"
                  >
                    {contributor.name}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
}
